import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CodeMirror from '@uiw/react-codemirror';
import { useApp } from '../contexts/AppContext';
import { Card } from '../types';
import { getLanguageExtension, getThemeExtension, getLanguageDisplayName, getCompactViewExtension } from '../utils/codeEditorUtils';
import { getDatabaseService } from '../services/DatabaseFactory';
import ConfirmationModal from './ConfirmationModal';

const CardEditor: React.FC = () => {
  const { deckId, cardId } = useParams<{ deckId: string; cardId?: string }>();
  const { state, loadDeck, createCard, updateCard, loadCards } = useApp();
  const [isEditing, setIsEditing] = useState(!!cardId);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showClearProgressModal, setShowClearProgressModal] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    question: '',
    category: 'Arrays',
    difficulty: 'Easy' as 'Easy' | 'Medium' | 'Hard',
    languages: {} as Record<string, string>,
    notes: '',
    tags: [] as string[],
    media: [] as string[]
  });

  // Monaco editor state
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    initializeForm();
  }, [deckId, cardId]);

  const initializeForm = async () => {
    try {
      setLoading(true);
      
      if (deckId) {
        await loadDeck(deckId);
      }
      
      if (cardId && isEditing) {
        // Load existing card
        const db = await getDatabaseService();
        const card = await db.getCard(cardId);
        if (card) {
          setFormData({
            name: card.name,
            question: card.question,
            category: card.category,
            difficulty: card.difficulty,
            languages: { ...card.languages },
            notes: card.notes,
            tags: [...card.tags],
            media: [...card.media]
          });
        }
      } else {
        // New card - set defaults based on deck
        if (state.currentDeck) {
          const defaultLanguages: Record<string, string> = {};
          state.currentDeck.targetLanguages.forEach(lang => {
            defaultLanguages[lang.toLowerCase()] = '';
          });
          
          setFormData(prev => ({
            ...prev,
            languages: defaultLanguages
          }));
        }
      }
    } catch (error) {
      console.error('Failed to initialize form:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Card name is required';
    }
    
    if (!formData.question.trim()) {
      newErrors.question = 'Question is required';
    }
    
    // Check if at least one language has code
    const hasCode = Object.values(formData.languages).some(code => code.trim());
    if (!hasCode) {
      newErrors.languages = 'At least one language implementation is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveCard = async (clearProgress = false) => {
    try {
      if (isEditing && cardId) {
        if (clearProgress) {
          const db = await getDatabaseService();
          await db.resetCardProgress(cardId);
        }
        await updateCard(cardId, formData);
      } else {
        await createCard({
          ...formData,
          deckId: deckId!
        });
      }

      // Reload cards to refresh the parent component
      await loadCards(deckId!);
      navigate(`/deck/${deckId}/edit`);
    } catch (error) {
      console.error('Failed to save card:', error);
      setErrors({ general: 'Failed to save card. Please try again.' });
    }
  };

  const handleSave = async () => {
    if (!validateForm() || !deckId) return;

    if (isEditing && cardId) {
      try {
        const db = await getDatabaseService();
        const performance = await db.getCardPerformance(cardId);
        if (performance && performance.length > 0) {
          setShowClearProgressModal(true);
          return;
        }
      } catch (error) {
        console.error('Failed to check card performance:', error);
      }
    }

    await saveCard();
  };

  const handleSaveAndClear = async () => {
    setShowClearProgressModal(false);
    await saveCard(true);
  };

  const handleSaveKeepProgress = async () => {
    setShowClearProgressModal(false);
    await saveCard(false);
  };

  const handleCancel = () => {
    navigate(`/deck/${deckId}/edit`);
  };

  const handleLanguageChange = (language: string, code: string) => {
    setFormData(prev => ({
      ...prev,
      languages: {
        ...prev.languages,
        [language]: code
      }
    }));
  };

  const handleTagsChange = (tagsString: string) => {
    const tags = tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    setFormData(prev => ({
      ...prev,
      tags
    }));
  };


  const categories = [
    'Arrays', 'Strings', 'Loops', 'Conditionals', 'Functions', 
    'Objects', 'Math', 'Boolean Logic', 'Variables', 'Exception Handling',
    'Input/Output', 'Null Handling', 'Bit Operations', 'Comments', 'Collections'
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center space-x-2">
          <div className="spinner"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading card editor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to={`/deck/${deckId}/edit`}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {isEditing ? 'Edit Card' : 'New Card'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {state.currentDeck?.name}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`btn ${showPreview ? 'btn-primary' : 'btn-secondary'}`}
            >
              {showPreview ? 'Edit' : 'Preview'}
            </button>
          </div>
        </div>

        {/* Error Messages */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {errors.general}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Form */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Card Details</h2>
              </div>
              <div className="card-body space-y-4">
                <div className="form-group">
                  <label className="form-label">Card Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                    placeholder="e.g., Array Creation and Initialization"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Question/Prompt *</label>
                  <textarea
                    value={formData.question}
                    onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                    className={`form-textarea ${errors.question ? 'border-red-500' : ''}`}
                    rows={4}
                    placeholder="Describe what the student should implement..."
                  />
                  {errors.question && (
                    <p className="mt-1 text-sm text-red-600">{errors.question}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="form-select"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Difficulty</label>
                    <select
                      value={formData.difficulty}
                      onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
                      className="form-select"
                    >
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.tags.join(', ')}
                    onChange={(e) => handleTagsChange(e.target.value)}
                    className="form-input"
                    placeholder="arrays, initialization, basics"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="form-textarea"
                    rows={3}
                    placeholder="Additional notes or explanations..."
                  />
                </div>
              </div>
            </div>

            {/* Code Implementation */}
            <div className="card">
              <div className="card-header">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Code Implementation
                  </h2>
                  <div className="flex items-center space-x-2">
                    {Object.keys(formData.languages).map(lang => (
                      <button
                        key={lang}
                        onClick={() => setSelectedLanguage(lang)}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          selectedLanguage === lang
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {getLanguageDisplayName(lang)}
                      </button>
                    ))}
                  </div>
                </div>
                {errors.languages && (
                  <p className="mt-2 text-sm text-red-600">{errors.languages}</p>
                )}
              </div>
              <div className="card-body p-0">
                <div className="codemirror-container codemirror-enhanced" style={{ height: '65px' }}>
                  <CodeMirror
                    height='100%'
                    value={formData.languages[selectedLanguage] || ''}
                    onChange={(value) => handleLanguageChange(selectedLanguage, value)}
                    extensions={[
                      getLanguageExtension(selectedLanguage),
                      ...(getThemeExtension(state.theme === 'dark') ? [getThemeExtension(state.theme === 'dark')!] : [])
                    ]}
                    theme={state.theme === 'dark' ? 'dark' : 'light'}
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: false,
                      dropCursor: false,
                      allowMultipleSelections: false,
                      indentOnInput: true,
                      bracketMatching: true,
                      closeBrackets: true,
                      autocompletion: false,
                      highlightSelectionMatches: false
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            {showPreview && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Card Preview</h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {formData.name || 'Untitled Card'}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          formData.difficulty === 'Easy' 
                            ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
                            : formData.difficulty === 'Medium'
                            ? 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
                            : 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
                        }`}>
                          {formData.difficulty}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded text-xs">
                          {formData.category}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {formData.question || 'No question provided'}
                    </p>
                    
                    {formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {Object.entries(formData.languages).some(([_, code]) => code.trim()) && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Reference Implementation:
                        </h4>
                        <div className="space-y-3">
                          {Object.entries(formData.languages)
                            .filter(([_, code]) => code.trim())
                            .map(([lang, code]) => (
                              <div key={lang}>
                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {getLanguageDisplayName(lang)}:
                                </div>
                                <div className="codemirror-container readonly solution-preview">
                                  <CodeMirror
                                    height='100%'
                                    value={code}
                                    extensions={[
                                      getLanguageExtension(lang),
                                      getCompactViewExtension(),
                                      ...(getThemeExtension(state.theme === 'dark') ? [getThemeExtension(state.theme === 'dark')!] : [])
                                    ]}
                                    theme={state.theme === 'dark' ? 'dark' : 'light'}
                                    editable={false}
                                    basicSetup={{
                                      lineNumbers: false,
                                      foldGutter: false,
                                      dropCursor: false,
                                      allowMultipleSelections: false,
                                      indentOnInput: false,
                                      bracketMatching: false,
                                      closeBrackets: false,
                                      autocompletion: false,
                                      highlightSelectionMatches: false,
                                      searchKeymap: false
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    
                    {formData.notes && (
                      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Notes:
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formData.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="card">
              <div className="card-body">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancel}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn btn-primary"
                    disabled={!formData.name.trim() || !formData.question.trim()}
                  >
                    {isEditing ? 'Update Card' : 'Create Card'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showClearProgressModal && (
        <ConfirmationModal
          title="Clear Study Progress?"
          message="This card has study history. Would you like to clear its progress data?"
          confirmText="Clear Progress"
          cancelText="Keep Progress"
          confirmButtonClass="btn-danger"
          onConfirm={handleSaveAndClear}
          onCancel={handleSaveKeepProgress}
        />
      )}
    </div>
  );
};

export default CardEditor;